<template>
  <div class="ui middle aligned center aligned grid">
    <div class="column" v-if="showForm">
      <div class="ui success message" v-if="showSuccessMessage">
        <p>{{ $t('pages.register_external.form.success') }}</p>
      </div>
      <div class="ui blue header centered">
        <div class="content">
          {{ $t('pages.register.header') }}
        </div>
      </div>
      <form class="ui large form" @submit.prevent="sendForm">
        <div class="ui stacked segment">
          <div class="field" :class="{ 'error' : errors.has('email') }">
            <div class="ui input">
              <input type="email" v-model="email" :data-vv-as="$t('pages.register.form.email')" :autocomplete="false" :autofocus="false" name="email"
                     :placeholder="$t('pages.register.form.email')" v-validate="'required|email'">
            </div>
            <span style="color:red;" v-if="errors.has('email')">{{ errors.first('email') }}</span>
          </div>

          <div class="field" :class="{ 'error' : errors.has('password') }">
            <div class="ui input">
              <input type="password" v-model="password" ref="passwordRef"
                     :data-vv-as="$t('pages.register.form.password')" name="password"
                     :placeholder="$t('pages.register.form.password')" v-validate="'required|min:8'">
            </div>
            <span style="color:red;" v-if="errors.has('password')">{{ errors.first('password') }}</span>
          </div>

          <div class="field" :class="{ 'error' : errors.has('password_confirmation') }">
            <div class="ui input">
              <input type="password" v-model="password_confirmation"
                     :data-vv-as="$t('pages.register.form.password_confirmation')" name="password_confirmation"
                     :placeholder="$t('pages.register.form.password_confirmation')"
                     v-validate="'required|min:8|confirmed:passwordRef'">
            </div>
            <span style="color:red;" v-if="errors.has('password_confirmation')">{{
                errors.first('password_confirmation')
              }}</span>
          </div>

          <div class="field" :class="{ 'error' : errors.has('terms') }">
            <div class="ui checkbox">
              <input name="terms" v-model="acceptingTerms" :data-vv-as="$t('pages.register.form.termsandconds')"
                     type="checkbox" v-validate="'required'">
              <label>{{ $t('pages.register.form.i_accept') }} <a @click.prevent="showTerms = !showTerms" href="#">{{
                  $t('pages.register.form.termsandconds')
                }}</a></label>
              <span style="color: red;" v-if="errors.has('terms')">{{ errors.first('terms') }}</span>
            </div>
          </div>

          <button type="submit" class="ui large fluid blue button" :disabled="errors.any()">
            {{ $t('pages.register.form.button') }}
          </button>
        </div>
      </form>

      <transition name="fade" mode="out-in">
        <div class="ui message centered" v-show="showLoginButton">
          <span>{{ $t('pages.register_external.already_have_account') }}</span>
          <router-link :to="{name: 'login'}">{{ $t('pages.login.header') }}</router-link>
        </div>
      </transition>
    </div>

    <semantic-modal v-model="showTerms"
                    :settings="{content_classes: {content: true}, closeable: false, closeable_button: false}"
                    class="large">
      <div class="ui header" slot="header">
        {{ $t('modals.termsandconds.accept_header') }}
      </div>
      <user-terms></user-terms>
      <div class="actions" slot="actions">
        <div class="ui red deny button" @click="acceptingTerms = false">{{ $t('globals.buttons.no') }}</div>
        <div class="ui ok green button" @click="acceptingTerms = true">{{ $t('globals.buttons.accept') }}</div>
      </div>
    </semantic-modal>
  </div>
</template>

<script>
import size from 'lodash/size'
import find from 'lodash/find'

export default {
  name: 'ExternalRegistration',

  layout: 'default',

  data() {
    return {
      showForm: false,
      email: '',
      password: '',
      password_confirmation: '',
      acceptingTerms: false,
      showTerms: false,

      merchantToAssign: null,
      referrerUrl: '',
      referrerHostname: '',

      showSuccessMessage: false,
      showLoginButton: false,
      waitBeforeRedirect: 2000
    }
  },

  mounted() {
    if (size(this.$auth.user()) > 0) {
      this.$auth.logout({
        makeRequest: true,
        redirect: false
      })
    }

    this.checkUserFrom()
      .then(() => {
        this.showForm = true

        if (size(this.$auth.user()) > 0) {
          this.$auth.logout({
            makeRequest: true,
            redirect: false
          })
        }
      }).catch(() => {
      this.$router.push('/')
      this.showForm = false
    })
  },

  methods: {
    checkUserFrom() {
      return new Promise((resolve, reject) => {
        if (this.isExternalUser) {
          const referrer = document.referrer || null

          if (referrer && referrer.length > 0) {
            this.referrerUrl = referrer

            try {
              this.referrerHostname = new URL(referrer).hostname
            } catch (e) {
              this.referrerHostname = null
            }
          }

          if (this.isFacebookUser) {
            if (!this.merchantKey) {
              reject()
            }

            this.merchantToAssign = this.merchantKey
            resolve()
          } else if (this.referrerHostname) {
            let merchantId = null

            switch (this.referrerHostname) {
              case 'zietwing.dev':
                if (process.env.NODE_ENV === 'demo') {
                  console.log('Demo env. Setting 19')
                  merchantId = 19
                } else {
                  merchantId = 121
                }

                if (merchantId !== 19) {
                  console.log('ENV not found :(')
                  merchantId = 19
                }
                break;
              case 'tsfestival.dk':
                merchantId = 188 // merchant ID from prod - Tange Sø Festival
                break;
              case 'hoerningparken.dk':
                merchantId = 215 // merchant ID from prod - Grundejerforeningen Hørningparken
                break;
            }

            if (merchantId) {
              this.merchantToAssign = merchantId
              resolve()
            } else {
              reject()
            }
          } else {
            reject()
          }
        } else {
          reject()
        }
      })
    },

    sendForm() {
      this.$validator.validateAll()
        .then(result => {
          if (result) {
            this.$auth.register({
              data: {
                email: this.email,
                password: this.password,
                password_confirmation: this.password_confirmation,
                assigned_merchant: this.merchantToAssign,
                referrer_url: this.referrerUrl,
                referrer_hostname: this.referrerHostname
              },
              autoLogin: false
            }).then(() => {
              this.showSuccessMessage = true

              setTimeout(() => {
                this.$auth.login({
                  data: {
                    email: this.email,
                    password: this.password
                  },
                  rememberMe: true,
                  redirect: {name: 'usersetup', params: {process: 'get-started'}},
                  fetchUser: true
                }).then(() => {
                  this.fetchActiveMerchant(this.merchantToAssign)
                })
              }, this.waitBeforeRedirect)
            }).catch((error) => {
              const _this = this

              $.map(error.response.data.errors, function (item, index) {
                if (index === 'email' && item[0] === 'user_already_exits') {
                  _this.errors.add({
                    field: index,
                    msg: _this.$t('pages.register_external.validation.errors.already_exist')
                  })
                  _this.showLoginButton = !_this.showLoginButton
                } else if (index === 'email' && item[0] === 'merchant_not_found') {
                  _this.errors.add({
                    field: index,
                    msg: _this.$t('pages.register_external.validation.errors.merchant_not_found')
                  })

                  setTimeout(() => {
                    _this.$router.replace('/404')
                  }, 3000)
                } else {
                  _this.errors.add({
                    field: index,
                    msg: item[0]
                  })
                }
              })
            })
          }
        }).catch()
    },

    fetchActiveMerchant(id) {
      return new Promise((resolve, reject) => {
        this.$store.dispatch('fetchMerchant', id).then(() => {
          // return active merchant
          resolve(this.$store.getters.activeMerchant)
        }, error => {
          reject(error)
        })
      })
    }
  },

  computed: {
    isExternalUser() {
      return !!this.$route.query.external_user
    },
    source() {
      return this.$route.query.source || null
    },
    merchantKey() {
      return this.$route.query.merchant_key || null
    },
    isFacebookUser() {
      return this.source === 'facebook'
    }
  }
}
</script>

<style lang="scss" scoped>
.ui.middle.aligned.center.aligned.grid {
  height: 100vh;
  width: 100%;
  justify-content: center;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active до версии 2.1.8 */ {
  opacity: 0;
}

.ui.middle.aligned.center.aligned.grid > .column {
  width: 450px;

  @media screen and (max-width: 768px) {
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/auth/ExternalRegistration.vue